import {
  Box,
  Button,
  DateFormats,
  DescriptionList,
  type DescriptionListProps,
  formatISODate,
  formatNumber,
  Inline,
  Modal,
  Stack,
} from '@omnipresentgroup/design-system';
import {
  CountriesPurpose,
  useSupportedCountries,
} from '@omnipresentgroup/domains';
import type {
  InvoicingCurrencyEnum,
  InvoicingFrequencyEnum,
} from '@omnipresentgroup/types';
import { useCallback } from 'react';

import { useManagerUser } from '../../../../hooks';
import { WrappedMixpanel } from '../../../../mixpanelWrapper';
import type { AddContractorFields } from '../../types';
import type { StepSummaryModalProps } from '../Stepper';
import { BillingSection } from './BillingSection';

export const MODAL_TITLE = 'Confirm contractor details';

const getDateDisplayText = (dateISO?: string): string => {
  return dateISO ? formatISODate(dateISO, DateFormats.Date) : '-';
};

const getAmountDisplayText = ({
  frequency,
  currency,
  amount,
}: {
  frequency?: InvoicingFrequencyEnum;
  currency: InvoicingCurrencyEnum;
  amount: number;
}): string => {
  const baseString = formatNumber({
    input: Number(amount),
    currency,
  });
  if (frequency) {
    return `${baseString} ${frequency.replaceAll('_', ' ').toLowerCase()}`;
  }
  return baseString;
};

export type AddContractorSummaryModal =
  StepSummaryModalProps<AddContractorFields> & {
    companyId: string;
  };

export const AddContractorReviewModal = (props: AddContractorSummaryModal) => {
  const { modalProps, handleCancel, handleSubmit, loading, values, companyId } =
    props;

  const { userId } = useManagerUser();

  const { contractorDetails, contractualAgreement, billingArrangements } =
    values.contractor;

  const invoicingCurrency = contractualAgreement.invoicingAmount.currency;
  const billingCurrency = billingArrangements.billingArrangementsCurrency;

  const { data: countries } = useSupportedCountries({
    purpose: CountriesPurpose.RESIDENCE,
  });

  // Map form values to List for the modal
  const getFormValuesList = useCallback((): DescriptionListProps['list'] => {
    const map = new Map();

    map.set(
      'Name',
      `${contractorDetails.contractorsFirstName} ${contractorDetails.contractorsLastName}`,
    );
    map.set('Role', contractorDetails.contractorsRole);
    map.set(
      'Email',
      <Box css={{ whiteSpace: 'nowrap' }}>
        {contractorDetails.contractorsEmail}
      </Box>,
    );
    map.set(
      'Country',
      countries?.find(
        (country) => country.code === contractorDetails.contractorsCountry,
      )?.name || '-',
    );
    contractualAgreement.invoicingAmount.amount &&
      map.set(
        'Amount',
        getAmountDisplayText({
          frequency: contractualAgreement.contractualAgreementFrequency,
          currency: contractualAgreement.invoicingAmount.currency,
          amount: contractualAgreement.invoicingAmount.amount,
        }),
      );
    map.set(
      'Start date',
      getDateDisplayText(
        contractualAgreement.contractualAgreementDateRange
          ?.contractualAgreementStartDate,
      ),
    );
    map.set(
      'End date',
      getDateDisplayText(
        contractualAgreement.contractualAgreementDateRange
          ?.contractualAgreementEndDate,
      ),
    );

    return [...map].map(([key, value]) => ({
      header: key,
      detail: value,
    }));
  }, [
    contractorDetails.contractorsCountry,
    contractorDetails.contractorsEmail,
    contractorDetails.contractorsFirstName,
    contractorDetails.contractorsLastName,
    contractorDetails.contractorsRole,
    contractualAgreement.contractualAgreementDateRange
      ?.contractualAgreementEndDate,
    contractualAgreement.contractualAgreementDateRange
      ?.contractualAgreementStartDate,
    contractualAgreement.contractualAgreementFrequency,
    contractualAgreement.invoicingAmount.amount,
    contractualAgreement.invoicingAmount.currency,
    countries,
  ]);

  return (
    <Modal
      {...modalProps}
      span={5}
      onOpenChange={(open) => {
        modalProps?.onOpenChange?.(open);
      }}
    >
      <Modal.Header title={MODAL_TITLE} />

      <Modal.Body>
        <Stack gap="24">
          <DescriptionList
            id="add-contractor-summary-list"
            fraction="1/2"
            list={getFormValuesList()}
          />
          {invoicingCurrency && billingCurrency && (
            <BillingSection
              invoicingCurrency={invoicingCurrency}
              billingCurrency={billingCurrency}
            />
          )}
        </Stack>
      </Modal.Body>

      <Modal.Footer>
        <Inline justify="space-between" w="stretch">
          <Button
            variant="secondary"
            onClick={() => {
              handleCancel?.();
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              handleSubmit?.();
              WrappedMixpanel.track('Add Contractor Finish Clicked', {
                clientId: companyId,
                userId: userId,
              });
            }}
            loading={loading}
          >
            Finish
          </Button>
        </Inline>
      </Modal.Footer>
    </Modal>
  );
};
